import React, { useState, useEffect } from 'react';
import Upload from './pages/Upload';
import Results from './pages/Results';
import HowItWorks from './pages/HowItWorks';
import { useToneAnalysis, ToneProfile } from './hooks/useToneAnalysis';
import { useContentGeneration, ContentGenerationRequest, GeneratedContent } from './hooks/useContentGeneration';
import './App.css';

// SVG Icons for feature cards
const RocketIcon = () => (
  <div className="feature-icon-container">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="feature-icon-svg">
      <path d="M9.315 7.584C12.195 3.883 16.695 1.5 21.75 1.5a.75.75 0 0 1 .75.75c0 5.056-2.383 9.555-6.084 12.436A6.75 6.75 0 0 1 9.75 22.5a.75.75 0 0 1-.75-.75v-4.131A15.838 15.838 0 0 1 6.382 15H2.25a.75.75 0 0 1-.75-.75 6.75 6.75 0 0 1 7.815-6.666ZM15 6.75a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" />
      <path d="M5.26 17.242a.75.75 0 1 0-.897-1.203 5.243 5.243 0 0 0-2.05 5.022.75.75 0 0 0 .625.627 5.243 5.243 0 0 0 5.022-2.051.75.75 0 1 0-1.202-.897 3.744 3.744 0 0 1-3.008 1.51c0-1.23.592-2.323 1.51-3.008Z" />
    </svg>
  </div>
);

const PaletteIcon = () => (
  <div className="feature-icon-container">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="feature-icon-svg">
      <path fillRule="evenodd" d="M2.25 4.125c0-1.036.84-1.875 1.875-1.875h5.25c1.036 0 1.875.84 1.875 1.875V17.25a4.5 4.5 0 1 1-9 0V4.125Zm4.5 14.25a1.125 1.125 0 1 0 0-2.25 1.125 1.125 0 0 0 0 2.25Z" clipRule="evenodd" />
      <path d="M10.719 21.75h9.156c1.036 0 1.875-.84 1.875-1.875v-5.25c0-1.036-.84-1.875-1.875-1.875h-.14l-8.742 8.743c-.09.089-.18.175-.274.257h.001Z" />
      <path d="M10.719 21.75h9.156c1.036 0 1.875-.84 1.875-1.875v-5.25c0-1.036-.84-1.875-1.875-1.875h-.14l-8.742 8.743c-.09.089-.18.175-.274.257h.001Z" />
    </svg>
  </div>
);

const DeviceIcon = () => (
  <div className="feature-icon-container">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="feature-icon-svg">
      <path d="M10.5 18.75a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z" />
      <path fillRule="evenodd" d="M8.625.75A3.375 3.375 0 0 0 5.25 4.125v15.75a3.375 3.375 0 0 0 3.375 3.375h6.75a3.375 3.375 0 0 0 3.375-3.375V4.125A3.375 3.375 0 0 0 15.375.75h-6.75ZM7.5 4.125C7.5 3.504 8.004 3 8.625 3H9.75v.375c0 .621.504 1.125 1.125 1.125h2.25c.621 0 1.125-.504 1.125-1.125V3h1.125c.621 0 1.125.504 1.125 1.125v15.75c0 .621-.504 1.125-1.125 1.125h-6.75A1.125 1.125 0 0 1 7.5 19.875V4.125Z" clipRule="evenodd" />
    </svg>
  </div>
);

// Using GeneratedContent interface from useContentGeneration hook

function App() {
  const [showInput, setShowInput] = useState(false);
  const [content, setContent] = useState<GeneratedContent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [transcript, setTranscript] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [toneProfile, setToneProfile] = useState<ToneProfile | null>(null);
  
  // API hooks
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { analyzeSamples } = useToneAnalysis();
  const { generatePosts } = useContentGeneration();

  // Debug logging
  useEffect(() => {
    console.log("State updated:", { showInput, showHowItWorks, hasContent: !!content });
  }, [showInput, showHowItWorks, content]);

  const handleSubmit = async (data: { url: string; toneSamples: string; transcript?: string }) => {
    setIsLoading(true);
    try {
      // Store the transcript from the transcription API
      if (data.transcript) {
        setTranscript(data.transcript);
        console.log('Transcript received:', data.transcript.substring(0, 100) + '...');
      } else {
        console.warn('No transcript received');
        throw new Error('No transcript available');
      }
      
      // Analyze tone if samples are provided
      let profile = null;
      if (data.toneSamples) {
        try {
          profile = await analyzeSamples([data.toneSamples]);
          setToneProfile(profile);
          console.log('Tone profile:', profile);
        } catch (toneErr) {
          console.error('Error analyzing tone:', toneErr);
          // Continue without tone profile
        }
      }
      
      // Generate content for different platforms
      const contentRequest: ContentGenerationRequest = {
        transcript: data.transcript,
        tone_profile: profile || {
          tone: "casual and confident",
          structure: "short punchy sentences",
          quirks: ["emojis", "hooks", "1-line CTAs"],
          vocab: ["creator", "scale", "repurpose"]
        },
        platforms: ['twitter', 'linkedin', 'reddit'],
        video_url: data.url // Pass the YouTube URL to the backend
      };
      
      const generatedContent = await generatePosts(contentRequest);
      
      // Use the generated content directly as it now matches our interface
      const contentToUse = generatedContent || {
        twitter: `Based on "${data.url}": ${data.transcript?.substring(0, 100)}... #content`,
        linkedin: `I recently analyzed this content that discusses: ${data.transcript?.substring(0, 150)}...`,
        reddit: `What do you think about this? "${data.transcript?.substring(0, 200)}..."`
      };
      
      setContent(contentToUse);
    } catch (error) {
      console.error('Error generating content:', error);
      alert('Failed to generate content. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setContent(null);
  };

  const goToHome = () => {
    setShowInput(false);
    setContent(null);
    setShowHowItWorks(false);
  };

  const goToHowItWorks = () => {
    console.log("Going to How It Works page");
    setShowHowItWorks(true);
    setShowInput(false);
    setContent(null);
  };

  return (
    <div className="app-container">
      {/* Navbar */}
      <nav className="navbar">
        <div className="navbar-logo" onClick={goToHome}>
          <div className="logo-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="logo-svg">
              <path d="M8.25 4.5a3.75 3.75 0 117.5 0v8.25a3.75 3.75 0 11-7.5 0V4.5z" />
              <path d="M6 10.5a.75.75 0 01.75.75v1.5a5.25 5.25 0 1010.5 0v-1.5a.75.75 0 011.5 0v1.5a6.751 6.751 0 01-6 6.709v2.291h3a.75.75 0 010 1.5h-7.5a.75.75 0 010-1.5h3v-2.291a6.751 6.751 0 01-6-6.709v-1.5A.75.75 0 016 10.5z" />
            </svg>
          </div>
          <span className="logo-text">Reverb</span>
        </div>
        <div className="navbar-actions">
          {!showInput && !content && !showHowItWorks && (
            <button 
              className="navbar-button"
              onClick={() => setShowInput(true)}
            >
              Get Started
            </button>
          )}
        </div>
      </nav>

      {isLoading ? (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h3 className="loading-title">Generating Content</h3>
            <p className="loading-message">Analyzing your input and creating optimized posts...</p>
          </div>
        </div>
      ) : content ? (
        <Results onBack={handleBack} content={content} />
      ) : showInput ? (
        <Upload onSubmit={handleSubmit} loading={isLoading} onBack={goToHome} />
      ) : showHowItWorks ? (
        <HowItWorks onBack={goToHome} />
      ) : (
        <section className="hero">
          <h1 className="hero-title">
            Turn your voice into content<br />
            that echoes everywhere
          </h1>
          <p className="hero-subtitle">
            Reverb transforms your podcasts, blogs, and long-form videos into short-form content — 
            perfectly written for Twitter, LinkedIn, and Reddit. All in your tone, with zero effort.
          </p>
          <div className="hero-actions">
            <button 
              className="cta-button"
              onClick={() => setShowInput(true)}
            >
              Try Reverb
            </button>
            <button 
              className="cta-button secondary-button"
              onClick={goToHowItWorks}
            >
              How it works
            </button>
          </div>
          
          <div className="features-container">
            <div className="feature-card">
              <div className="feature-icon-container">
                <RocketIcon />
              </div>
              <h3 className="feature-title">Fast & Easy</h3>
              <p className="feature-desc">Generate social posts in seconds, not hours.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon-container">
                <PaletteIcon />
              </div>
              <h3 className="feature-title">Your Style</h3>
              <p className="feature-desc">Maintain your unique voice across all platforms.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon-container">
                <DeviceIcon />
              </div>
              <h3 className="feature-title">Platform Optimized</h3>
              <p className="feature-desc">Tailored content for each social network.</p>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-logo">
            <span className="footer-logo-text">Reverb</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
